/**
 * Evaluation status service
 * Handles status validation, transitions, and scoring lock logic
 */

import type { Evaluation } from '@/types/evaluation.types';
import { EvaluationStatus } from '@/types/evaluation.types';

/**
 * Validation result for status transitions
 */
export interface ValidationResult {
  /** Whether the validation passed */
  isValid: boolean;
  /** List of blockers preventing the transition */
  blockers: string[];
}

/**
 * Check if an evaluation can be activated
 * @param evaluation - The evaluation to validate
 * @returns Validation result with blockers
 */
export function canActivateEvaluation(evaluation: Evaluation): ValidationResult {
  const blockers: string[] = [];

  // Must have at least one solution
  if (evaluation.targetSolutions.length === 0) {
    blockers.push('dashboard:status.activate.blocker.noSolutions');
  }

  // Must have at least one criteria group
  if (evaluation.criteriaGroups.length === 0) {
    blockers.push('dashboard:status.activate.blocker.noGroups');
  }

  // Must have at least one criterion in any group
  const totalCriteria = evaluation.criteriaGroups.reduce(
    (sum, group) => sum + group.criteria.length,
    0
  );
  if (totalCriteria === 0) {
    blockers.push('dashboard:status.activate.blocker.noCriteria');
  }

  return {
    isValid: blockers.length === 0,
    blockers,
  };
}

/**
 * Check if an evaluation can be concluded
 * @param evaluation - The evaluation to validate
 * @returns Validation result
 */
export function canConcludeEvaluation(evaluation: Evaluation): ValidationResult {
  const blockers: string[] = [];

  // Must be in Active status
  if (evaluation.status !== EvaluationStatus.Active) {
    blockers.push('Evaluation must be Active to conclude');
  }

  // TODO: Add scorecard validation when implementing scoring completion checks
  // For now, we allow conclusion even if some scorecards are incomplete

  return {
    isValid: blockers.length === 0,
    blockers,
  };
}

/**
 * Check if an evaluation can be reactivated
 * @param evaluation - The evaluation to check
 * @returns Whether reactivation is allowed
 */
export function canReactivateEvaluation(evaluation: Evaluation): boolean {
  // Can only reactivate from Concluded or Closed states
  return (
    evaluation.status === EvaluationStatus.Concluded ||
    evaluation.status === EvaluationStatus.Closed
  );
}

/**
 * Check if an evaluation can be closed
 * @param evaluation - The evaluation to check
 * @returns Whether closing is allowed
 */
export function canCloseEvaluation(evaluation: Evaluation): boolean {
  // Can close from any state except already closed
  return evaluation.status !== EvaluationStatus.Closed;
}

/**
 * Check if scoring can proceed for an evaluation
 * @param evaluation - The evaluation to check
 * @returns Whether scoring is allowed
 */
export function canScoringProceed(evaluation: Evaluation): boolean {
  return !isScoringLocked(evaluation.status);
}

/**
 * Check if scoring is locked for a given status
 * @param status - The evaluation status
 * @returns Whether scoring is locked
 */
export function isScoringLocked(status: EvaluationStatus): boolean {
  return (
    status === EvaluationStatus.Concluded || status === EvaluationStatus.Closed
  );
}

/**
 * Get list of activation blockers
 * @param evaluation - The evaluation to check
 * @returns List of i18n keys for blockers
 */
export function getActivationBlockers(evaluation: Evaluation): string[] {
  return canActivateEvaluation(evaluation).blockers;
}

/**
 * Transition evaluation to Active status
 * @param evaluation - The evaluation to activate
 * @returns Updated evaluation
 * @throws Error if validation fails
 */
export function activateEvaluation(evaluation: Evaluation): Evaluation {
  const validation = canActivateEvaluation(evaluation);
  if (!validation.isValid) {
    throw new Error(
      `Cannot activate evaluation: ${validation.blockers.join(', ')}`
    );
  }

  return {
    ...evaluation,
    status: EvaluationStatus.Active,
    statusChangedDate: new Date(),
    modifiedDate: new Date(),
  };
}

/**
 * Transition evaluation to Concluded status
 * @param evaluation - The evaluation to conclude
 * @returns Updated evaluation
 * @throws Error if validation fails
 */
export function concludeEvaluation(evaluation: Evaluation): Evaluation {
  const validation = canConcludeEvaluation(evaluation);
  if (!validation.isValid) {
    throw new Error(
      `Cannot conclude evaluation: ${validation.blockers.join(', ')}`
    );
  }

  return {
    ...evaluation,
    status: EvaluationStatus.Concluded,
    statusChangedDate: new Date(),
    modifiedDate: new Date(),
  };
}

/**
 * Transition evaluation to Closed status
 * @param evaluation - The evaluation to close
 * @param reason - Optional reason for closing
 * @returns Updated evaluation
 * @throws Error if validation fails
 */
export function closeEvaluation(
  evaluation: Evaluation,
  reason?: string
): Evaluation {
  if (!canCloseEvaluation(evaluation)) {
    throw new Error('Cannot close evaluation: already closed');
  }

  return {
    ...evaluation,
    status: EvaluationStatus.Closed,
    statusChangedDate: new Date(),
    modifiedDate: new Date(),
    closedReason: reason || null,
  };
}

/**
 * Reactivate a concluded or closed evaluation
 * @param evaluation - The evaluation to reactivate
 * @returns Updated evaluation
 * @throws Error if validation fails
 */
export function reactivateEvaluation(evaluation: Evaluation): Evaluation {
  if (!canReactivateEvaluation(evaluation)) {
    throw new Error(
      'Cannot reactivate evaluation: must be Concluded or Closed'
    );
  }

  return {
    ...evaluation,
    status: EvaluationStatus.Active,
    statusChangedDate: new Date(),
    modifiedDate: new Date(),
    // Clear closed reason when reactivating
    closedReason: null,
  };
}
